import React, { useState } from "react";
import { Checkbox, styled, SvgIcon, Typography, Tooltip } from "@mui/material";
import { ChevronDown, ChevronRight, Folder, Edit, AddNew, Delete } from "orion-components/CBComponents/Icons";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import EditGroupNode from "./EditGroupNode";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { TargetingIcon } from "orion-components/SharedComponents";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";
import { canManageByApplication } from "orion-components/Profiles/Selectors";
import PropTypes from "prop-types";
import { getTranslation } from "orion-components/i18n";
import { deleteDeviceGroup } from "../../../GlobalData/DeviceGroups/actions";

const CustomizedCheckBox = styled(Checkbox)(() => ({
	width: "17px !important",
	height: "17px !important",
	marginRight: "12.6px",
	padding: "0px !important",
	marginLeft: "-2px !important"
}));

// This function is used to apply the margin property to nodes that have children and the root "All Devices" node.
const getNodeIndentation = (level) => 10 * (level ?? 0);
// If the node does not have children, the above function is used to adjust the spacing where the chevron would be and modify its actual margin accordingly.
const getChildNodeIndentation = (parentLevel, useCustomMargin, customMargin) =>
	useCustomMargin ? customMargin : 34 + parentLevel * 10;

const styles = {
	targetingIcon: (dir) => ({
		...(dir === "ltr" && { paddingLeft: 0 }),
		...(dir === "rtl" && { paddingRight: 0 })
	}),
	container: (node, hoveredNodeId, selectedDevice, displayChevrons) => ({
		alignItems: "center",
		display: "flex",
		height: 32,
		...((node?.children?.length === 0 || !node?.children) && node?.level !== 0
			? { marginLeft: getChildNodeIndentation(node?.parentLevel, node?.useCustomMargin, node?.customMargin) }
			: { marginLeft: getNodeIndentation(node?.level) }),
		backgroundColor: hoveredNodeId === node?.id || selectedDevice?.id === node?.id ? "#5D646A" : "transparent",
		borderRadius: 4, // Optional for better styling,
		width: "100%",
		...(!node?.children && { paddingLeft: 6 }),
		...(!displayChevrons && { paddingLeft: 10 }),
		...(node?.isCompositeDevice && node?.children.length === 0 ? { paddingLeft: 6 } : {}) //When a composite device has no children, this padding is applied to fill the chevron space.
	}),
	chevron: (dir) => ({
		...(dir === "ltr" && { marginLeft: 7.48, marginRight: 12.6 }),
		...(dir === "rtl" && { marginRight: 7.48, marginLeft: 12.6 }),
		width: 10.5
	}),
	checkbox: (node, dir) => ({
		...(node?.children && node.children.length === 0
			? { ...(dir === "ltr" && { marginLeft: 7.48 }), ...(dir === "rtl" && { marginRight: 7.48 }) }
			: {})
	}),
	folder: (node, dir) => ({
		paddingTop: 10,
		...(dir === "rtl" && { marginLeft: 10 }),
		...(node?.children && node.children.length === 0
			? { ...(dir === "ltr" && { marginLeft: 7.48 }), ...(dir === "rtl" && { marginRight: 7.48 }) }
			: {})
	}),
	svgIconWrapper: {
		width: 24,
		height: 24
	},
	spriteIcon: (node, dir) => ({
		width: 20,
		height: 20,
		backgroundColor: node?.deviceStateColor || "#6A6C70",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: "5px",
		...(dir === "ltr" && { marginRight: 12 }),
		...(dir === "rtl" && { marginLeft: 12 }),
		flexShrink: 0
	}),
	nodeOperations: (dir) => ({
		...(dir === "ltr" && { marginLeft: "auto" }),
		...(dir === "rtl" && { marginRight: "auto" }),
		marginRight: 11.9,
		display: "flex",
		alignItems: "center"
	}),
	editButton: (dir) => ({
		...(dir === "ltr" && { marginRight: 13.63 }),
		...(dir === "rtl" && { marginLeft: 13.63 })
	})
};

const RenderLabelWithIcon = ({
	node,
	isExpanded,
	hoveredNodeId,
	setHoveredNodeId,
	selectedNodeForEdit,
	setSelectedNodeForEdit,
	addOrUpdateNodeLabel,
	addNewNode,
	readOnly,
	addToDeviceGroup,
	getSelectedNodes,
	isForFacilitiesFloorPlan,
	selectedDevice,
	setSelectedDevice,
	displayChevrons,
	getSelectedMenuItem,
	allowDeviceSelection
}) => {
	const [isGroupSelected, setIsGroupSelected] = useState(false);
	const dir = useSelector((state) => getDir(state));

	const dispatch = useDispatch();

	const handleEditClick = (event) => {
		event.stopPropagation();
		setSelectedNodeForEdit(node?.id);
	};

	const clearSelectedNodeForEdit = (event, selectedNode) => {
		// Prevent the event from propagating to parent elements
		event.stopPropagation();

		// Optional: Prevent the default behavior if the event is a form submission or a link click
		event.preventDefault();
		setSelectedNodeForEdit(null);
		if (!selectedNode?.name?.trim()) {
			dispatch(deleteDeviceGroup(selectedNode.id));
		}
	};

	const handleAddNewClick = (event) => {
		event.stopPropagation();
		if (node?.children && node.children.length < 10) {
			addNewNode(node?.id);
		} else {
			dispatch(
				createUserFeedback("10 nested nodes is the limit, and no further nodes can be added.", null, true)
			);
		}
	};

	const selectDeviceGroups = (e) => {
		e.preventDefault();
		e.stopPropagation();

		setIsGroupSelected(!isGroupSelected);
		getSelectedNodes(node?.id);
	};

	const canManageCamerasApp = useSelector((state) => canManageByApplication(state, "cameras-app", "manage"));

	const handleRemove = (node) => {
		const menuItem =
			node?.isDevice && node?.compositeDeviceId === node?.parentGroupId
				? "removeFromCompositeDevice"
				: "removeFromGroup";

		getSelectedMenuItem(menuItem, node);
	};

	const handleDeviceSelection = () => {
		if (allowDeviceSelection && node?.entityType === "device") {
			setSelectedDevice(node);
			getSelectedNodes(node?.id);
		} else if (
			isForFacilitiesFloorPlan &&
			(node?.entityType === "device" ||
				node?.entityType === "camera" ||
				node?.entityType === "compositeDevice") &&
			(!node?.compositeDeviceId || node?.compositeDeviceId !== node?.parentGroupId)
		) {
			setSelectedDevice(node);
		}
	};

	return (
		<div
			style={styles.container(node, hoveredNodeId, selectedDevice, displayChevrons)}
			onMouseEnter={() => setHoveredNodeId(node?.id)}
			onMouseLeave={() => setHoveredNodeId(null)}
			onClick={handleDeviceSelection}
		>
			{selectedNodeForEdit !== node?.id ? (
				<>
					{node?.children?.length > 0 && displayChevrons && (
						<div style={styles.chevron(dir)}>
							{isExpanded ? (
								<ChevronDown />
							) : dir === "rtl" ? (
								<ChevronLeft style={{ marginTop: 8, width: 20, height: 24 }} />
							) : (
								<ChevronRight />
							)}
						</div>
					)}

					{addToDeviceGroup && node?.id !== "root" && (
						<div style={styles.checkbox(node, dir)}>
							<CustomizedCheckBox
								checked={isGroupSelected}
								onClick={(e) => {
									selectDeviceGroups(e);
								}}
							/>
						</div>
					)}

					{node?.isGroup && (
						<div style={styles.folder(node, dir)}>
							<SvgIcon style={styles.svgIconWrapper}>
								<Folder />
							</SvgIcon>
						</div>
					)}

					{node?.isDevice && !isForFacilitiesFloorPlan ? (
						<>
							<div style={{ marginInlineStart: 1 }}>
								<TargetingIcon
									feedId={node?.feedId}
									id={node?.id}
									geometry={node?.entityData?.geometry}
									iconContainerStyle={styles.targetingIcon(dir)}
									customStyles={{ width: "18px", height: "18px" }}
								/>
							</div>
						</>
					) : (
						<></>
					)}

					{node?.isDevice && (
						<div style={styles.spriteIcon(node, dir)}>
							<SpriteIcon iconName={node?.icon} customWidth="10px" />
						</div>
					)}

					<Typography
						sx={{
							color: "#FFFFFF",
							"&.MuiTypography-body1": {
								margin: "0px !important"
							},
							textOverflow: "ellipsis",
							overflow: "hidden",
							whiteSpace: "nowrap",
							minWidth: 0,
							flexGrow: 1
						}}
					>
						{node?.name}
					</Typography>
					{node?.id === hoveredNodeId && !readOnly && node?.isGroup && canManageCamerasApp && (
						<div style={styles.nodeOperations(dir)}>
							<div style={styles.editButton(dir)} onClick={handleEditClick}>
								<Edit />
							</div>
							<div style={{ cursor: "pointer" }} onClick={handleAddNewClick}>
								<AddNew />
							</div>
						</div>
					)}

					{((node?.isDevice && node?.parentGroupId !== "root") ||
						(node?.entityType === "compositeDevice" && node?.parentGroupId !== "root")) &&
						node?.id === hoveredNodeId &&
						!readOnly && (
							<div style={styles.nodeOperations(dir)}>
								<div style={{ cursor: "pointer" }} onClick={() => handleRemove(node)}>
									<Tooltip
										title={getTranslation(
											"global.deviceManager.deviceTree.components.rightClickMenu.removeFromGroup"
										)}
										placement="top"
									>
										<Delete />
									</Tooltip>
								</div>
							</div>
						)}
				</>
			) : (
				<EditGroupNode
					node={node}
					clearSelectedNodeForEdit={clearSelectedNodeForEdit}
					addOrUpdateNodeLabel={addOrUpdateNodeLabel}
				/>
			)}
		</div>
	);
};

RenderLabelWithIcon.propTypes = {
	node: PropTypes.shape({
		id: PropTypes.string,
		name: PropTypes.string,
		level: PropTypes.number,
		parentLevel: PropTypes.number,
		isDevice: PropTypes.bool,
		isGroup: PropTypes.bool,
		entityType: PropTypes.string,
		feedId: PropTypes.string,
		entityData: PropTypes.shape({
			geometry: PropTypes.object
		}),
		icon: PropTypes.string,
		deviceStateColor: PropTypes.string,
		children: PropTypes.array
	}),
	isExpanded: PropTypes.bool,
	hoveredNodeId: PropTypes.string,
	setHoveredNodeId: PropTypes.func.isRequired,
	selectedNodeForEdit: PropTypes.string,
	setSelectedNodeForEdit: PropTypes.func,
	addOrUpdateNodeLabel: PropTypes.func,
	addNewNode: PropTypes.func,
	readOnly: PropTypes.bool,
	addToDeviceGroup: PropTypes.bool,
	getSelectedNodes: PropTypes.func.isRequired,
	isForFacilitiesFloorPlan: PropTypes.bool,
	selectedDevice: PropTypes.object,
	setSelectedDevice: PropTypes.func,
	getSelectedDevice: PropTypes.func,
	displayChevrons: PropTypes.bool,
	getSelectedMenuItem: PropTypes.func
};
RenderLabelWithIcon.defaultProps = {
	node: {
		id: "",
		name: "",
		level: 0,
		parentLevel: 0,
		isDevice: false,
		isGroup: false,
		entityType: "",
		feedId: "",
		entityData: { geometry: {} },
		icon: "",
		deviceStateColor: "#6A6C70",
		children: []
	},
	isExpanded: false,
	hoveredNodeId: null,
	selectedNodeForEdit: null,
	readOnly: false,
	addToDeviceGroup: false,
	isForFacilitiesFloorPlan: false,
	selectedDevice: "",
	setSelectedDevice: () => {},
	getSelectedDevice: () => {},
	displayChevrons: true,
	getSelectedMenuItem: () => {}
};

export default RenderLabelWithIcon;
